import React from 'react';
import classnames from 'classnames';
import { sanitizeHref } from '@wix/da-url';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import DeviationMediaService from '../../../utils/DeviationMediaService';
import { getSource } from '../../_helpers';
import { getCoverImageAsBackgroundStyleProp } from '../../hooks/useDeviationCoverImage';
// TODO: Restore video player BI — uncomment useVideoPlayerActionBiLogger and pass onPlaybackAction to Film
// import { useVideoPlayerActionBiLogger } from '../hooks/useVideoPlayerActionBiLogger';
import { FullViewVideoProps } from '../types';
import type { VideoPlayerAction } from '../hooks/useVideoPlayerActionBiLogger';
import LoadablePlayer from './LoadablePlayer';

import s from './Film.scss';

export interface FilmProps extends FullViewVideoProps {
  /** Optional; wire via useVideoPlayerActionBiLogger when BI is restored */
  onPlaybackAction?: (action: VideoPlayerAction) => void;
}

/** Renders film (video) fullview for a deviation or attachment media source */
export const Film: React.FC<FilmProps> = ({
  source,
  width,
  height,
  className,
  noAutoplay = false,
  onPlaybackAction,
}) => {
  const videoFile = getSource(DeviationMediaService.getMediaByType(source, 'video'));
  const previewFile = DeviationMediaService.getDeviationFile(source, 'preview');

  const consistent_devpage_blur = useFlag('consistent_devpage_blur');
  if (!videoFile) {
    // show the blurred preview/poster if backend doesn't return the videofile
    if (consistent_devpage_blur && previewFile) {
      const style = {
        ...getCoverImageAsBackgroundStyleProp(previewFile.src),
        backgroundSize: 'contain',
      };
      return <div className={classnames(s['root'], s['preview'], className)} style={style} />;
    }
    return null;
  }

  return (
    <ErrorBoundary debugComponent="Film">
      {previewFile && <link rel="preload" href={sanitizeHref(previewFile.src)} as="image" />}
      <link rel="preload" href={sanitizeHref(videoFile.src)} as="video" crossOrigin="anonymous" />
      <div className={classnames(s['root'], className)} style={{ width, height }}>
        <LoadablePlayer
          src={videoFile.src}
          poster={previewFile?.src}
          autoplay={!noAutoplay}
          muted
          loop={videoFile.duration && videoFile.duration > 60 ? false : true}
          width={videoFile.width}
          height={videoFile.height}
          onPlaybackAction={onPlaybackAction}
        />
      </div>
    </ErrorBoundary>
  );
};
Film.displayName = 'Film';
